import { z } from 'zod'

import { serializeDataItems } from '../shared/entity-serializer'
import {
  isQueryValidationError,
  orderArg,
  warnOrderDowngraded,
} from '../shared/orderArg'
import { resolveRelation } from '../shared/resolveRelation'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

export const getRelatedTool = defineTool({
  name: 'get_related',
  description:
    'Fetch related entities for a given parent record. For example, get all tickets for a company, or activities for a person.',
  inputSchema: () =>
    z.object({
      parentDataClass: z
        .string()
        .describe('Entity type of the parent, e.g. Company, Person'),
      parentId: z.string().describe('The _id of the parent record'),
      relation: z
        .string()
        .describe(
          'The relation name as defined in the registry, e.g. activities, tickets, persons',
        ),
      limit: z.number().optional().describe('Max items to return (default 20)'),
      order: orderArg
        .optional()
        .describe(
          'Sort order. Use date or name fields only: updatedAt, createdAt, beginAt, name. Do NOT use value, amount, or other numeric/currency fields — they are not sortable and will cause errors.',
        ),
    }),

  async run(args, ctx) {
    const { parentDataClass, parentId, relation, limit = 20, order } = args

    const resolved = resolveRelation(
      parentDataClass,
      parentId,
      relation,
      ctx.featureFlags,
    )
    if ('error' in resolved) {
      return resolved
    }

    // warning: resolveRelation returns Scrivito DataClass objects in .jdc/.dc;
    // these are passed opaque to queryResolved/getResolved — the host adapter
    // casts them back to DataClass internally. Byte-identical to the old load() path.
    const { data } = getHostRuntime()
    let items: unknown[]

    if (resolved.kind === 'junction') {
      const { jdc, junctionFilters, idField, parentFilterKey, dc } = resolved

      const rawRows = await data.queryResolved(jdc, {
        filters: junctionFilters,
        limit: limit * 2,
      })
      // warning: queryResolved passes opaque DataClass; rows are DataItem-like with .get()/.id()
      const junctionRows = rawRows as Array<{ get(f: string): unknown }>
      // Dedupe via Set: symmetric junctions can yield the same related _id from
      // two rows (parent stored on both sides). Mirrors uniqBy(items, 'id') in
      // useContactTabData.
      const relatedIds = [
        ...new Set(
          junctionRows
            .map((r) => {
              const target = r.get(idField) as string
              // ContactContactRelation links are symmetric: PISA's parent filter
              // returns rows with the parent on EITHER side, so the related record
              // may sit in parentFilterKey instead of idField. Mirror
              // getRelatedContact (useContactTabData) — take the side that is NOT
              // the parent. Directional junctions never carry the parent in
              // idField, so this is a no-op for them.
              return target === parentId
                ? (r.get(parentFilterKey) as string)
                : target
            })
            .filter(Boolean),
        ),
      ]

      const relatedRaw = (
        await Promise.all(
          relatedIds.map((id) => data.getResolved(dc, id).catch(() => null)),
        )
      ).filter(Boolean) as Array<{ id(): string; get(f: string): unknown }>

      // Apply order client-side for junction-fetched items
      const effectiveOrder =
        order ?? (resolved.defaultOrder as [string, 'asc' | 'desc'][])
      const [orderField, orderDir] = effectiveOrder[0] ?? ['updatedAt', 'desc']
      items = relatedRaw
        .sort((a, b) => {
          const av = a.get(orderField) as string | number | null
          const bv = b.get(orderField) as string | number | null
          if (av == null && bv == null) {
            return 0
          }
          if (av == null) {
            return 1
          }
          if (bv == null) {
            return -1
          }
          return orderDir === 'asc' ? (av < bv ? -1 : 1) : av > bv ? -1 : 1
        })
        .slice(0, limit)
    } else {
      const { dc, filters } = resolved
      const effectiveOrder =
        order ?? (resolved.defaultOrder as [string, 'asc' | 'desc'][])
      try {
        items = await data.queryResolved(dc, {
          filters,
          order: effectiveOrder,
          limit,
        })
      } catch (err) {
        // PISA returns 422 when the order field is not supported (e.g. value, amount).
        // `.take()` surfaces that as a wrapped DataScopeError ("A validation has
        // failed"), not a raw ClientError, so match on that. Only retry without
        // order for that case — rethrow network/auth errors rather than masking
        // them with a second, unordered query.
        if (!isQueryValidationError(err)) {
          throw err
        }
        warnOrderDowngraded('get_related', effectiveOrder)
        items = await data.queryResolved(dc, { filters, limit })
      }
    }

    return serializeDataItems(items, resolved.targetDataClass, ctx)
  },
})
